import { cx } from '@linaria/core';
import { useRef, useEffect, useReducer } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { useGetClassesQuery, useGetCoWorkersQuery, getStudentsInClass } from '@pm2/api';
import { useUserIdentityState } from '@provider/user-identity';
import { Drawer } from '@ui/kit';
import { SizeTransition } from '@utils/react';
import { SelectPanel } from '../../../../molecules/select-panel/_select-panel.js';
import { useSelectedCourse } from '../../../../state/selectors.js';
import { useCourseBuilder, courseBuilderStore } from '../../../../state/store.js';
import { shareStore, shareSlice } from './_share-drawer.state.js';
import { transitionWrapperCls, panelTransitionCls, panelTransitionDurationMs, wrapperCls } from './_share-drawer.style.js';
import { CopySection } from './copy-section.js';
import { ShareSection } from './share-section.js';
import { jsx, jsxs } from 'react/jsx-runtime';

function ShareDrawer() {
  const activeSelector = shareStore.useSelector(state => state.activeSelector);
  const isSelectorOpenedRef = useRef(!!activeSelector);
  const showShareDrawer = useCourseBuilder(state => state.showShareDrawer);
  useEffect(() => {
    isSelectorOpenedRef.current = !!activeSelector;
  }, [activeSelector]);
  const direction = isSelectorOpenedRef.current ? "backwards" : "forwards";
  const handleClose = evt => {
    evt.stopPropagation();
    if (activeSelector) {
      shareStore.dispatch(shareStore.actions.changeSelector(undefined));
    } else {
      courseBuilderStore.dispatch(courseBuilderStore.actions.toggleShareDrawer(false));
    }
  };
  const selectedCourse = useSelectedCourse();
  return /*#__PURE__*/jsx(Drawer, {
    explicitKey: "share-drawer",
    active: showShareDrawer,
    onClose: handleClose,
    children: /*#__PURE__*/jsx("div", {
      className: cx(wrapperCls, direction),
      children: /*#__PURE__*/jsxs(TransitionGroup, {
        className: transitionWrapperCls,
        children: [!activeSelector && /*#__PURE__*/jsx(SizeTransition, {
          axis: "vertical",
          swappable: true,
          timeout: panelTransitionDurationMs,
          classNames: panelTransitionCls,
          children: ref => /*#__PURE__*/jsxs("div", {
            ref: ref,
            className: panelTransitionCls,
            children: [selectedCourse && /*#__PURE__*/jsx(CopySection, {
              course: selectedCourse
            }), /*#__PURE__*/jsx(ShareSection, {
              onClose: handleClose
            })]
          })
        }, "base"), activeSelector === "class" && /*#__PURE__*/jsx(SizeTransition, {
          axis: "vertical",
          swappable: true,
          timeout: panelTransitionDurationMs,
          classNames: panelTransitionCls,
          children: ref => /*#__PURE__*/jsx(ClassSelectorPanel, {
            ref: ref,
            onClose: handleClose
          })
        }, "classes"), activeSelector === "student" && /*#__PURE__*/jsx(SizeTransition, {
          axis: "vertical",
          swappable: true,
          timeout: panelTransitionDurationMs,
          classNames: panelTransitionCls,
          children: ref => /*#__PURE__*/jsx(StudentsSelectorPanel, {
            ref: ref,
            onClose: handleClose
          })
        }, "students"), activeSelector === "coWorker" && /*#__PURE__*/jsx(SizeTransition, {
          axis: "vertical",
          swappable: true,
          timeout: panelTransitionDurationMs,
          classNames: panelTransitionCls,
          children: ref => /*#__PURE__*/jsx(CoWorkerSelectorPanel, {
            ref: ref,
            onClose: handleClose
          })
        }, "coworkers")]
      })
    })
  });
}
function ClassSelectorPanel(props) {
  const user = useUserIdentityState(state => state.user);
  const classes = useGetClassesQuery();
  const [draftState, draftDispatch] = useReducer(shareSlice.reducer, shareStore.getState());
  const handleSave = async evt => {
    shareStore.dispatch(shareStore.actions.changeClass(draftState.selectedClass));
    props.onClose(evt);

    // In case a new class has been selected, then immediately fetch the
    // related students for the given class
    if (user && draftState.selectedClass) {
      const studentsInClass = await getStudentsInClass(user, draftState.selectedClass.id);
      shareStore.dispatch(shareStore.actions.applyUsersInClass({
        class: draftState.selectedClass,
        students: studentsInClass
      }));
    }
  };
  return /*#__PURE__*/jsx("div", {
    ref: props.ref,
    className: panelTransitionCls,
    children: /*#__PURE__*/jsx(SelectPanel, {
      label: "V\xE6lg hold",
      inputPlaceholder: "S\xF8g efter hold",
      selectedValues: draftState.selectedClass ? [draftState.selectedClass.id] : [],
      options: classes.data?.map(it => ({
        ...it,
        label: it.name
      })) ?? [],
      toggleValue: (id, newClass) => {
        draftDispatch(shareSlice.actions.changeClass(newClass));
      },
      onSave: handleSave,
      onCancel: props.onClose
    })
  });
}
function StudentsSelectorPanel(props) {
  const [draftState, draftDispatch] = useReducer(shareSlice.reducer, shareStore.getState());
  const handleSave = evt => {
    shareStore.dispatch(shareStore.actions.replaceSelectedStudents(draftState.selectedStudents ?? []));
    props.onClose(evt);
  };
  return /*#__PURE__*/jsx("div", {
    ref: props.ref,
    className: panelTransitionCls,
    children: /*#__PURE__*/jsx(SelectPanel, {
      label: "V\xE6lg elever",
      inputPlaceholder: "S\xF8g efter elev",
      selectedValues: draftState.selectedStudents?.map(it => it.id),
      options: draftState.studentsInClass?.map(it => ({
        ...it,
        label: it.name
      })) ?? [],
      toggleValue: (id, newStudent) => {
        draftDispatch(shareStore.actions.toggleStudent(newStudent));
      },
      onSave: handleSave,
      onCancel: props.onClose,
      multiSelect: true
    })
  });
}
function CoWorkerSelectorPanel(props) {
  const coWorkers = useGetCoWorkersQuery();
  const [draftState, draftDispatch] = useReducer(shareSlice.reducer, shareStore.getState());
  const handleSave = evt => {
    shareStore.dispatch(shareStore.actions.replaceSelectedCoWorkers(draftState.selectedCoWorkers ?? []));
    shareStore.dispatch(shareStore.actions.replaceSelectedStudents(draftState.selectedStudents ?? []));
    props.onClose(evt);
  };
  return /*#__PURE__*/jsx("div", {
    ref: props.ref,
    className: panelTransitionCls,
    children: /*#__PURE__*/jsx(SelectPanel, {
      label: "V\xE6lg kollega",
      inputPlaceholder: "S\xF8g efter kollega",
      selectedValues: draftState.selectedCoWorkers?.map(it => it.id),
      options: coWorkers.data?.map(it => ({
        ...it,
        label: it.name
      })) ?? [],
      toggleValue: (id, newCoWorker) => draftDispatch(shareStore.actions.toggleCoWorker(newCoWorker)),
      onSave: handleSave,
      onCancel: props.onClose,
      multiSelect: true
    })
  });
}

export { ShareDrawer };
//# sourceMappingURL=_share-drawer.js.map
